import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { ApiError, ApiResponse } from '../types';

// This is the single source of truth for API configuration.
// It automatically switches between local and production based on environment.
const getBaseURL = () => {
  const isProduction = process.env.NODE_ENV === 'production' && window.location.hostname === 'live.mydigicash.in';
  
  // Use custom domain for production, otherwise use local
  return isProduction ? 'https://live.mydigicash.in/api' : 'http://localhost:5000/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
});

// Interceptor to automatically add the auth token to every request.
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      const networkError: ApiError = {
        message: 'Network error: Could not connect to the server. Please check your internet connection.',
        status: 0, // Or a custom status code for network errors
      };
      return Promise.reject(networkError);
    }
    
    if (error.response?.status === 401) {
      // Don't redirect if it's a 2FA authentication error
      const is2FAError = error.response?.data?.require2FA || 
                         error.response?.data?.message?.includes('2FA') ||
                         error.config?.url?.includes('/auth/login');
      
      if (!is2FAError) {
        // Token expired or invalid (but not 2FA related)
        sessionStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    
    // Create standardized error object
    const apiError: ApiError = {
      message: error.response?.data?.message || 'An unexpected error occurred',
      status: error.response?.status || 500,
      details: error.response?.data,
    };
    
    return Promise.reject(apiError);
  }
);

// Generic API methods
export const apiMethods = {
  // GET request
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.get<T>(url, config);
    return response.data;
  },

  // POST request
  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.post<T>(url, data, config);
    return response.data;
  },

  // PUT request
  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.put<T>(url, data, config);
    return response.data;
  },

  // PATCH request
  patch: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.patch<T>(url, data, config);
    return response.data;
  },

  // DELETE request
  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.delete<T>(url, config);
    return response.data;
  },
};

// Auth API methods
export const authAPI = {
  login: async (mobileNumber: string, password: string) => {
    return apiMethods.post<{ token: string }>('/auth/login', { mobileNumber, password });
  },
};

// Query API methods
export const queryAPI = {
  getQueries: async (filters?: any) => {
    return apiMethods.get('/queries', { params: filters });
  },

  createQuery: async (queryData: any) => {
    return apiMethods.post('/queries', queryData);
  },

  updateQuery: async (id: string, updateData: any) => {
    return apiMethods.patch(`/queries/${id}`, updateData);
  },

  deleteQuery: async (id: string) => {
    return apiMethods.delete(`/queries/${id}`);
  },

  bulkDeleteQueries: async (ids: string[]) => {
    return apiMethods.post('/queries/bulk', { ids });
  },

  getSummary: async (filters?: any) => {
    return apiMethods.get('/queries/summary', { params: filters });
  },
};

// Staff API methods
export const staffAPI = {
  getStaff: async () => {
    return apiMethods.get('/staff');
  },
  getStaffNames: async () => {
    return apiMethods.get('/staff/names');
  },

  createStaff: async (staffData: any) => {
    return apiMethods.post('/staff', staffData);
  },

  updateStaff: async (id: string, updateData: any) => {
    return apiMethods.patch(`/staff/${id}`, updateData);
  },

  deleteStaff: async (id: string) => {
    return apiMethods.delete(`/staff/${id}`);
  },

  updateStaffStatus: async (id: string, isActive: boolean) => {
    return apiMethods.patch(`/staff/${id}/status`, { isActive });
  },
};

// Common Query API methods
export const commonQueryAPI = {
  getCommonQueries: async () => {
    return apiMethods.get('/common-queries');
  },

  createCommonQuery: async (queryData: any) => {
    return apiMethods.post('/common-queries', queryData);
  },

  updateCommonQuery: async (id: string, updateData: any) => {
    return apiMethods.put(`/common-queries/${id}`, updateData);
  },

  deleteCommonQuery: async (id: string) => {
    return apiMethods.delete(`/common-queries/${id}`);
  },

  bulkCreateCommonQueries: async (data: any[]) => {
    return apiMethods.post('/common-queries/bulk', { data });
  },
};

// External API methods
export const externalAPI = {
  getMemberDetails: async (mobileNumber: string) => {
    return apiMethods.post('/proxy/member-details', { mobile_number: mobileNumber });
  },
};

export const excelGeneratorAPI = {
  // Check for global duplicates before adding to the table
  checkGlobalDuplicate: async (orderId: string, date: string) => {
    return apiMethods.post<{ 
      isDuplicate: boolean; 
      timeAgo?: string; 
      addedBy?: string;
      fileType?: string;
    }>('/excel-records/check-duplicate', { orderId, date });
  },

  // Add a new record after it's been processed and downloaded
  addExcelRecord: async (recordData: any) => {
    return apiMethods.post('/excel-records', recordData);
  },

  // Get all records for a specific date
  getRecordsByDate: async (date: string): Promise<any[]> => {
    return apiMethods.get(`/excel-records?date=${date}`);
  },

  // Get all records (no date filter)
  getAllRecords: async (): Promise<any[]> => {
    return apiMethods.get('/excel-records/all');
  },

  // Get the current value of a file counter (e.g., 'icici')
  getFileCounter: async (name: string) => {
    return apiMethods.get<{ currentCounter: number; nextFileNumber: string }>(`/file-counter/${name}`);
  },

  // Increment a file counter after a successful download
  incrementFileCounter: async (name: string) => {
    return apiMethods.post<{ fileNumber: string; usedCounter: number; nextCounter: number }>(`/file-counter/${name}/increment`);
  },
  getDebugFileCounters: async () => {
    return apiMethods.get('/debug/file-counters');
  },
};

export { api }; 